export interface ProjectEstimate {
  laborHours: number;
  materialCost: number;
  teamSize: number;
  totalCost: number;
}

export async function getProjectEstimate(projectDetails: {
  service: string;
  description: string;
  hasMaterials: boolean;
  projectSize: string;
  timeline: string;
  propertyType: string;
}): Promise<ProjectEstimate> {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-estimate`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(projectDetails)
    });

    if (!response.ok) {
      throw new Error('Failed to get estimate from server');
    }

    const estimate: ProjectEstimate = await response.json();

    if (isNaN(estimate.laborHours) || isNaN(estimate.teamSize) || isNaN(estimate.totalCost)) {
      return calculateBasicEstimate(projectDetails);
    }

    return estimate;
  } catch (error) {
    console.error('Error getting project estimate:', error);
    return calculateBasicEstimate(projectDetails);
  }
}

function calculateBasicEstimate(details: {
  service: string;
  description: string;
  hasMaterials: boolean;
  projectSize: string;
  timeline: string;
  propertyType: string;
  images?: string[];
}): ProjectEstimate {
  let basePrice = 0;
  let laborHours = 0;
  let teamSize = 2;
  let materialCost = 0;

  // Base price by service type
  switch (details.service) {
    case 'Kitchen Renovation':
      basePrice = 25000;
      laborHours = 160;
      teamSize = 4;
      materialCost = 15000;
      break;
    case 'Bathroom Remodeling':
      basePrice = 15000;
      laborHours = 80;
      teamSize = 3;
      materialCost = 8000;
      break;
    case 'Home Addition':
      basePrice = 50000;
      laborHours = 320;
      teamSize = 6;
      materialCost = 30000;
      break;
    case 'Basement Finishing':
      basePrice = 30000;
      laborHours = 200;
      teamSize = 4;
      materialCost = 18000;
      break;
    default:
      basePrice = 10000;
      laborHours = 80;
      teamSize = 2;
      materialCost = 5000;
  }

  // Adjust for project size
  let multiplier = 1;
  switch (details.projectSize) {
    case 'Small (Single Room/Area)':
      multiplier = 0.8;
      break;
    case 'Medium (Multiple Rooms)':
      multiplier = 1.2;
      break;
    case 'Large (Whole Floor/House)':
      multiplier = 1.5;
      break;
  }

  // Apply multiplier
  basePrice *= multiplier;
  laborHours = Math.round(laborHours * multiplier);
  materialCost = Math.round(materialCost * multiplier);

  // Adjust for materials
  if (details.hasMaterials) {
    materialCost = 0;
    basePrice *= 0.6;
  }

  return {
    laborHours,
    materialCost,
    teamSize,
    totalCost: Math.round(basePrice)
  };
}